import React, { useState } from 'react';
import { ShoppingCart, FileText, Stethoscope } from 'lucide-react';
import { Product } from '../stripe-config';

interface ProductCardProps {
  product: Product;
  onPurchase: (priceId: string) => Promise<void>;
  isPurchased?: boolean;
}

export const ProductCard: React.FC<ProductCardProps> = ({ 
  product, 
  onPurchase, 
  isPurchased = false 
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handlePurchase = async () => {
    if (isPurchased) return;
    
    setIsLoading(true);
    try {
      await onPurchase(product.priceId);
    } catch (error) {
      console.error('Purchase failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getIcon = () => {
    if (product.name.toLowerCase().includes('surgery')) {
      return <Stethoscope className="w-8 h-8 text-blue-600" />;
    }
    return <FileText className="w-8 h-8 text-blue-600" />;
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between mb-4">
        {getIcon()}
        <span className="text-2xl font-bold text-gray-900">
          £{product.price.toFixed(2)}
        </span>
      </div>
      
      <h3 className="text-xl font-semibold text-gray-900 mb-2">
        {product.name}
      </h3>
      
      <p className="text-gray-600 mb-6 text-sm leading-relaxed">
        {product.description}
      </p>
      
      <button
        onClick={handlePurchase}
        disabled={isLoading || isPurchased}
        className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
          isPurchased
            ? 'bg-green-100 text-green-800 cursor-not-allowed'
            : isLoading
            ? 'bg-gray-400 text-white cursor-not-allowed'
            : 'bg-blue-600 text-white hover:bg-blue-700'
        }`}
      >
        {isLoading ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Processing...
          </>
        ) : isPurchased ? (
          'Purchased'
        ) : (
          <>
            <ShoppingCart className="w-4 h-4" />
            Purchase Now
          </>
        )}
      </button>
    </div>
  );
};